import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type SubscriptionPlan = 'free' | 'solo' | 'team' | 'agency';

interface BoostRateLimit {
  canBoost: boolean;
  remainingQuota: number;
  totalQuota: number;
  isInCooldown: boolean;
  cooldownRemainingMs: number;
  plan: SubscriptionPlan;
  getQuotaMessage: () => string;
  getCooldownMessage: () => string;
  executeBoost: () => void;
}

const BOOST_QUOTAS: Record<SubscriptionPlan, number> = {
  free: 3,
  solo: 5,
  team: 10,
  agency: 20
};

const COOLDOWN_MS = 5000; // 5 seconds

const getTodayKey = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0]; // YYYY-MM-DD
};

const getStorageKey = (userId: string, date: string): string => {
  return `boost_quota_${userId}_${date}`;
};

const getCooldownKey = (userId: string): string => {
  return `boost_cooldown_${userId}`;
};

/**
 * Hook for managing Boost Mode rate limiting with quotas and cooldowns
 * 
 * Features:
 * - Daily quotas based on subscription plan
 * - 5-second cooldown between boosts
 * - Internationalized messages
 * - localStorage persistence
 * - Automatic quota reset at midnight
 */
export const useBoostRateLimit = (
  userId: string,
  plan: SubscriptionPlan = 'free'
): BoostRateLimit => {
  const { t } = useTranslation();
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  // Update current time every second for cooldown tracking
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const todayKey = getTodayKey();
  const totalQuota = BOOST_QUOTAS[plan];
  
  // Get current usage from localStorage
  const usedQuota = useMemo(() => {
    if (!userId) return 0;
    
    const storageKey = getStorageKey(userId, todayKey);
    const stored = localStorage.getItem(storageKey);
    return stored ? parseInt(stored, 10) : 0;
  }, [userId, todayKey, currentTime]); // Include currentTime to refresh after executeBoost
  
  // Get last boost timestamp
  const lastBoostTimestamp = useMemo(() => {
    if (!userId) return 0;
    
    const cooldownKey = getCooldownKey(userId);
    const stored = localStorage.getItem(cooldownKey);
    return stored ? parseInt(stored, 10) : 0;
  }, [userId, currentTime]); // Include currentTime to refresh after executeBoost
  
  // Calculate remaining quota
  const remainingQuota = Math.max(0, totalQuota - usedQuota);
  
  // Calculate cooldown status
  const cooldownEndTime = lastBoostTimestamp + COOLDOWN_MS;
  const isInCooldown = currentTime < cooldownEndTime;
  const cooldownRemainingMs = Math.max(0, cooldownEndTime - currentTime);
  
  // Determine if user can boost
  const canBoost = remainingQuota > 0 && !isInCooldown;
  
  // Generate quota message
  const getQuotaMessage = useCallback((): string => {
    const quotaType = plan === 'free' ? 'free' : 'paid';
    const key = `boostMode.quota.${quotaType}`;
    return t(key, { 
      remaining: remainingQuota, 
      total: totalQuota,
      defaultValue: `${remainingQuota}/${totalQuota} boosts left today` 
    });
  }, [t, plan, remainingQuota, totalQuota]);
  
  // Generate cooldown message
  const getCooldownMessage = useCallback((): string => {
    if (!isInCooldown) return '';
    
    const secondsRemaining = Math.ceil(cooldownRemainingMs / 1000);
    return t('boostMode.cooldown.wait', { 
      seconds: secondsRemaining,
      defaultValue: `Please wait ${secondsRemaining} seconds` 
    });
  }, [t, isInCooldown, cooldownRemainingMs]);
  
  // Execute a boost (increment counter and set cooldown)
  const executeBoost = useCallback(() => {
    if (!canBoost || !userId) return;
    
    const now = Date.now();
    
    // Increment usage counter
    const storageKey = getStorageKey(userId, todayKey);
    const newUsage = usedQuota + 1;
    localStorage.setItem(storageKey, newUsage.toString());
    
    // Set cooldown timestamp
    const cooldownKey = getCooldownKey(userId);
    localStorage.setItem(cooldownKey, now.toString());
    
    // Force re-render by updating currentTime
    setCurrentTime(now);
  }, [canBoost, userId, todayKey, usedQuota]);
  
  return {
    canBoost,
    remainingQuota,
    totalQuota,
    isInCooldown,
    cooldownRemainingMs,
    plan,
    getQuotaMessage,
    getCooldownMessage,
    executeBoost
  };
};